import React, { useEffect, useState } from 'react';
import { TrendingUp, Users, FileText, AlertTriangle, DollarSign, Globe, FolderKanban, Target, PieChart } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart as RechartPie, Pie, Cell, Legend } from 'recharts';
import { useAuth } from '@/context/AuthContext';
import { formatCurrency } from '@/utils/helpers';
import api from '@/utils/api';
import { toast } from 'sonner';

const COLORS = ['#6C3483', '#2874A6', '#00F2A9', '#F39C12', '#E74C3C'];

const KPICard = ({ icon: Icon, label, value, subValue, color = "primary", trend, trendUp }) => (
  <div className="relative overflow-hidden bg-white p-6 rounded-2xl border border-slate-100 shadow-sm" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className={`h-12 w-12 rounded-full bg-${color}/10 flex items-center justify-center text-${color} mb-4`}>
      <Icon className="h-6 w-6" />
    </div>
    <p className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
    {subValue && <p className="text-sm text-slate-500 mt-1">{subValue}</p>}
    {trend && (
      <p className={`text-sm mt-2 font-medium ${trendUp ? 'text-success' : 'text-error'}`}>{trend}</p>
    )}
  </div>
);

export default function Dashboard() {
  const { company } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await api.get('/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      toast.error('Erro ao carregar estatísticas');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-slate-600">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  const financeData = [
    { name: 'Faturado', value: stats?.faturamento_mes || 0 },
    { name: 'Previsto', value: stats?.faturamento_previsto || 0 },
    { name: 'Despesas', value: stats?.despesas_mes || 0 },
    { name: 'Lucro', value: stats?.lucro_mes || 0 },
  ];

  const leadsData = [
    { name: 'Abertos', value: stats?.leads_abertos || 0 },
    { name: 'Novos/Mês', value: stats?.leads_mes || 0 },
  ];

  return (
    <div className="space-y-8" data-testid="dashboard-container">
      <div>
        <h1 className="text-4xl font-heading font-bold tracking-tight text-slate-900 mb-2">
          Olá, {company?.name || 'Kode Studio'}!
        </h1>
        <p className="text-slate-600">Visão geral do seu negócio</p>
      </div>

      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <KPICard
          icon={DollarSign}
          label="MRR"
          value={formatCurrency(stats?.mrr || 0)}
          subValue="Receita Mensal Recorrente"
          color="primary"
        />
        <KPICard
          icon={TrendingUp}
          label="Faturamento Mês"
          value={formatCurrency(stats?.faturamento_mes || 0)}
          subValue={`Previsto: ${formatCurrency(stats?.faturamento_previsto || 0)}`}
          color="accent"
        />
        <KPICard
          icon={FileText}
          label="Contratos Ativos"
          value={stats?.contratos_ativos || 0}
          subValue={`${stats?.contratos_a_vencer || 0} a vencer`}
          color="secondary"
        />
        <KPICard
          icon={Users}
          label="Clientes Ativos"
          value={stats?.clientes_ativos || 0}
          subValue={`Total: ${stats?.total_clientes || 0}`}
          color="info"
        />
      </div>

      {/* KPIs Secundários */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-8 w-8 rounded-full bg-green-100 flex items-center justify-center">
              <TrendingUp className="h-4 w-4 text-green-600" />
            </div>
            <p className="text-sm font-medium text-slate-500">Lucro Mês</p>
          </div>
          <p className={`text-2xl font-bold ${(stats?.lucro_mes || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
            {formatCurrency(stats?.lucro_mes || 0)}
          </p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
              <FolderKanban className="h-4 w-4 text-blue-600" />
            </div>
            <p className="text-sm font-medium text-slate-500">Projetos Ativos</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats?.projetos_em_andamento || 0}</p>
          {stats?.projetos_atrasados > 0 && (
            <p className="text-xs text-red-500 mt-1">{stats?.projetos_atrasados} atrasados</p>
          )}
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-8 w-8 rounded-full bg-purple-100 flex items-center justify-center">
              <Target className="h-4 w-4 text-purple-600" />
            </div>
            <p className="text-sm font-medium text-slate-500">Leads Abertos</p>
          </div>
          <p className="text-2xl font-bold text-slate-900">{stats?.leads_abertos || 0}</p>
          <p className="text-xs text-slate-500 mt-1">{stats?.leads_mes || 0} novos este mês</p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-8 w-8 rounded-full bg-accent/10 flex items-center justify-center">
              <PieChart className="h-4 w-4 text-accent" />
            </div>
            <p className="text-sm font-medium text-slate-500">Taxa Conversão</p>
          </div>
          <p className="text-2xl font-bold text-accent">{(stats?.taxa_conversao || 0).toFixed(1)}%</p>
        </div>

        <div className="bg-white p-5 rounded-xl border border-slate-100 shadow-sm">
          <div className="flex items-center gap-3 mb-2">
            <div className="h-8 w-8 rounded-full bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="h-4 w-4 text-orange-600" />
            </div>
            <p className="text-sm font-medium text-slate-500">Em Atraso</p>
          </div>
          <p className={`text-2xl font-bold ${stats?.clientes_em_atraso > 0 ? 'text-orange-600' : 'text-slate-900'}`}>
            {stats?.clientes_em_atraso || 0}
          </p>
          <p className="text-xs text-slate-500 mt-1">clientes</p>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">Financeiro do Mês</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={financeData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 12 }} />
              <YAxis stroke="#64748b" tickFormatter={(v) => `R$${(v/1000).toFixed(0)}k`} />
              <Tooltip formatter={(value) => formatCurrency(value)} />
              <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                {financeData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-xl border border-slate-100 shadow-sm">
          <h3 className="text-lg font-heading font-semibold text-slate-900 mb-4">Resumo Rápido</h3>
          <div className="space-y-4">
            <div className="p-4 bg-gradient-to-r from-primary/5 to-primary/10 rounded-lg border border-primary/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Despesas do Mês</p>
                  <p className="text-xl font-bold text-primary">{formatCurrency(stats?.despesas_mes || 0)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-primary/30" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Hospedagens a Vencer</p>
                <p className="text-xl font-bold text-slate-900">{stats?.hospedagens_a_vencer || 0}</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">Contratos a Vencer</p>
                <p className="text-xl font-bold text-slate-900">{stats?.contratos_a_vencer || 0}</p>
              </div>
            </div>

            <div className="p-4 bg-gradient-to-r from-accent/5 to-accent/10 rounded-lg border border-accent/20">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Faturamento Previsto</p>
                  <p className="text-xl font-bold text-accent">{formatCurrency(stats?.faturamento_previsto || 0)}</p>
                </div>
                <TrendingUp className="h-8 w-8 text-accent/30" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
